import { useState } from 'react'
import { Eye, EyeOff, Moon, Sun } from 'lucide-react'
import { supabase } from '../services/supabase'

export function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [isForgotPassword, setIsForgotPassword] = useState(false)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return localStorage.getItem('sfp_dark_mode') === 'true'
  })

  const toggleDarkMode = () => {
    const newMode = !isDarkMode
    setIsDarkMode(newMode)
    localStorage.setItem('sfp_dark_mode', newMode ? 'true' : 'false')
  }

  const handleSignIn = async (e) => {
    e.preventDefault()
    setError('')
    setMessage('')
    setLoading(true)
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password })
      if (error) throw error
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordReset = async (e) => {
    e.preventDefault()
    if (!email) { setError('Please enter your email address'); return }
    setError('')
    setMessage('')
    setLoading(true)
    try {
      const appUrl = import.meta.env.VITE_APP_URL || window.location.origin
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${appUrl}/auth/reset-password`,
      })
      if (error) throw error
      setMessage('Password reset link sent — check your inbox.')
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={`min-h-screen flex relative transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-gray-100'
    }`}>

      {/* Dark mode toggle */}
      <button
        onClick={toggleDarkMode}
        className="absolute top-6 right-6 z-50 p-2.5 rounded-full transition-all duration-200"
        style={{
          backgroundColor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(245,166,35,0.1)',
          color: isDarkMode ? '#fff' : '#F5A623',
        }}
        title={isDarkMode ? 'Light mode' : 'Dark mode'}
      >
        {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
      </button>

      {/* ── LEFT: Brand panel ───────────────────────────────────── */}
      <div
        className="hidden lg:flex lg:w-[55%] flex-col items-center justify-center relative overflow-hidden p-16 transition-all duration-300"
        style={{
          background: isDarkMode
            ? 'linear-gradient(150deg, #1e293b 0%, #0f172a 55%, #020617 100%)'
            : 'linear-gradient(150deg, #ffffff 0%, #fdf8f0 55%, #fef9f2 100%)'
        }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(rgba(245,166,35,0.15) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />

        {/* Top-left ambient glow */}
        <div className="absolute pointer-events-none" style={{
          top: '-20%', left: '-20%',
          width: '70%', height: '70%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.10) 0%, transparent 65%)',
        }} />

        {/* Bottom-right ambient glow */}
        <div className="absolute pointer-events-none" style={{
          bottom: '-20%', right: '-15%',
          width: '60%', height: '60%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.07) 0%, transparent 65%)',
        }} />

        {/* Content */}
        <div className="relative z-10 w-full max-w-lg">

          {/* Logo */}
          <img
            src={isDarkMode ? '/SFP Wealth Logo - For Digital.png' : '/sfp logo light jpeg.png'}
            alt="SFP Wealth"
            className="mb-14"
            style={{ width: '200px' }}
          />

          {/* Headline */}
          <h1 style={{
            fontSize: '2.25rem',
            fontWeight: 700,
            color: isDarkMode ? '#ffffff' : '#333333',
            lineHeight: '1.15',
            letterSpacing: '-0.03em',
            marginBottom: '1rem',
          }}>
            Wealth Management,<br />
            <span style={{ color: '#F5A623' }}>Simplified.</span>
          </h1>

          <p style={{
            color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(51,51,51,0.50)',
            fontSize: '1rem',
            lineHeight: '1.7',
            maxWidth: '380px',
            marginBottom: '3rem',
          }}>
            Your complete platform for client relationships, portfolio oversight, and financial planning — all in one place.
          </p>

          {/* Feature bullets */}
          <div className="space-y-4">
            {[
              { dot: '#F5A623', text: 'Centralised client and policy management with real-time sync' },
              { dot: '#10b981', text: 'Claims tracking integrated directly with the Dispatch portal' },
              { dot: '#60a5fa', text: 'Role-based access control and full advisor permissions' },
            ].map((item) => (
              <div key={item.text} className="flex items-start gap-3">
                <div className="mt-2 shrink-0 rounded-full" style={{
                  width: 7, height: 7,
                  backgroundColor: item.dot,
                }} />
                <p style={{
                  color: isDarkMode ? 'rgba(255,255,255,0.50)' : 'rgba(51,51,51,0.50)',
                  fontSize: '0.875rem',
                  lineHeight: '1.6',
                }}>
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom badge */}
        <div className="absolute bottom-8 left-16 flex items-center gap-2" style={{
          color: isDarkMode ? 'rgba(245,166,35,0.40)' : 'rgba(245,166,35,0.55)',
          fontSize: '0.7rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
        }}>
          <div className="rounded-full" style={{ width: 6, height: 6, backgroundColor: '#F5A623', opacity: isDarkMode ? 0.4 : 0.6 }} />
          SFP Wealth CRM
        </div>
      </div>

      {/* ── RIGHT: Form panel ───────────────────────────────────── */}
      <div
        className="flex-1 flex flex-col items-center justify-center px-8 py-12 transition-colors duration-300"
        style={{ backgroundColor: isDarkMode ? '#0f172a' : '#F5F5F5' }}
      >
        {/* Mobile logo */}
        <div className="lg:hidden mb-10 text-center">
          <img src={isDarkMode ? '/SFP Wealth Logo - For Digital.png' : '/sfp logo light jpeg.png'} alt="SFP Wealth" className="h-12 w-auto mx-auto" />
        </div>

        <div className="w-full max-w-sm">

          {/* Heading */}
          <div className="mb-8">
            <h2 style={{
              fontSize: '1.625rem',
              fontWeight: 700,
              color: isDarkMode ? '#ffffff' : '#333333',
              letterSpacing: '-0.02em',
              marginBottom: '0.375rem',
            }}>
              {isForgotPassword ? 'Reset password' : 'Welcome back'}
            </h2>
            <p style={{ color: isDarkMode ? 'rgba(255,255,255,0.45)' : 'rgba(51,51,51,0.45)', fontSize: '0.875rem' }}>
              {isForgotPassword
                ? "Enter your email and we'll send a reset link"
                : 'Sign in to your SFP Wealth account'}
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="rounded-xl px-4 py-3 mb-5 text-sm" style={{
              backgroundColor: isDarkMode ? 'rgba(239,68,68,0.15)' : 'rgba(239,68,68,0.08)',
              border: isDarkMode ? '1px solid rgba(239,68,68,0.40)' : '1px solid rgba(239,68,68,0.20)',
              color: isDarkMode ? '#ff6b6b' : '#dc2626',
            }}>
              {error}
            </div>
          )}

          {/* Success */}
          {message && (
            <div className="rounded-xl px-4 py-3 mb-5 text-sm" style={{
              backgroundColor: isDarkMode ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.08)',
              border: isDarkMode ? '1px solid rgba(16,185,129,0.40)' : '1px solid rgba(16,185,129,0.20)',
              color: isDarkMode ? '#5eead4' : '#059669',
            }}>
              {message}
            </div>
          )}

          {/* Form */}
          <form onSubmit={isForgotPassword ? handlePasswordReset : handleSignIn} className="space-y-4">

            {/* Email */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@sfpwealth.co.za"
                required
                autoComplete="email"
                className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors duration-200"
                style={{
                  backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                  border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                  color: isDarkMode ? '#ffffff' : '#333333',
                }}
                onFocus={(e) => { e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
              />
            </div>

            {/* Password */}
            {!isForgotPassword && (
              <div>
                <label htmlFor="password" className="block text-sm font-medium mb-1.5" style={{ color: isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}>
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    autoComplete="current-password"
                    className="w-full rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none transition-colors duration-200"
                    style={{
                      backgroundColor: isDarkMode ? '#1e293b' : '#ffffff',
                      border: isDarkMode ? '1px solid rgba(255,255,255,0.15)' : '1px solid #CCCCCC',
                      color: isDarkMode ? '#ffffff' : '#333333',
                    }}
                    onFocus={(e) => { e.target.style.borderColor = 'rgba(245,166,35,0.70)' }}
                    onBlur={(e) => { e.target.style.borderColor = isDarkMode ? 'rgba(255,255,255,0.15)' : '#CCCCCC' }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                    style={{ color: isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.65)' : 'rgba(51,51,51,0.65)' }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.30)' : 'rgba(51,51,51,0.30)' }}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot password link */}
            {!isForgotPassword && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => { setIsForgotPassword(true); setError(''); setMessage('') }}
                  className="text-sm transition-colors"
                  style={{ color: isDarkMode ? '#fbbf24' : '#F5A623' }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? '#fcd34d' : '#d4891a' }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? '#fbbf24' : '#F5A623' }}
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: '#F5A623',
                color: '#ffffff',
                marginTop: isForgotPassword ? '0.25rem' : '0',
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.backgroundColor = '#d4891a' }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#F5A623' }}
            >
              {loading
                ? (isForgotPassword ? 'Sending…' : 'Signing in…')
                : (isForgotPassword ? 'Send reset link' : 'Sign In')}
            </button>
          </form>

          {/* Back to sign in */}
          {isForgotPassword && (
            <button
              type="button"
              onClick={() => { setIsForgotPassword(false); setError(''); setMessage('') }}
              className="w-full text-center text-sm mt-4 transition-colors"
              style={{ color: isDarkMode ? 'rgba(255,255,255,0.40)' : 'rgba(51,51,51,0.40)' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.70)' : 'rgba(51,51,51,0.70)' }}
              onMouseLeave={(e) => { e.currentTarget.style.color = isDarkMode ? 'rgba(255,255,255,0.40)' : 'rgba(51,51,51,0.40)' }}
            >
              ← Back to sign in
            </button>
          )}
        </div>

        {/* Footer */}
        <p className="absolute bottom-6 text-center text-xs" style={{ color: isDarkMode ? 'rgba(255,255,255,0.25)' : 'rgba(51,51,51,0.25)' }}>
          © {new Date().getFullYear()} SFP Wealth. All rights reserved.
        </p>
      </div>
    </div>
  )
}
